import { fetchSupportAttachmentBlob } from '../services/supportApi';
import type { SupportAttachmentDTO, SupportLocalMessage } from '../types/support';
import {
  collectAttachmentIdsForCache,
  pickMessagesForCache,
} from './supportChatCache';
import {
  getCachedAttachmentBlob,
  hasCachedAttachment,
  pruneAttachmentMediaCache,
  putCachedAttachmentBlob,
  shouldCacheAttachment,
} from './supportAttachmentMediaCache';

/** Вложения из последних N сообщений (как у текстового кэша). */
export const collectAttachmentsForCache = (
  messages: SupportLocalMessage[],
  hasMore = false,
): SupportAttachmentDTO[] => {
  const { messages: tail } = pickMessagesForCache(messages, hasMore);
  const seen = new Set<string>();
  const attachments: SupportAttachmentDTO[] = [];

  for (const message of tail) {
    for (const attachment of message.attachments ?? []) {
      if (!attachment.id || seen.has(attachment.id)) continue;
      seen.add(attachment.id);
      attachments.push(attachment);
    }
  }

  return attachments;
};

let syncInFlight: Promise<void> | null = null;
let syncQueued = false;
let pendingSync: {
  userId: string | number;
  threadId: string;
  messages: SupportLocalMessage[];
  hasMore: boolean;
} | null = null;

const prefetchAttachment = async (
  userId: string | number,
  threadId: string,
  attachment: SupportAttachmentDTO,
): Promise<void> => {
  if (!shouldCacheAttachment(attachment)) return;
  if (await hasCachedAttachment(userId, attachment.id)) return;

  const blob = await fetchSupportAttachmentBlob(attachment.id);
  if (!shouldCacheAttachment(attachment, blob.size)) return;
  await putCachedAttachmentBlob(userId, threadId, attachment, blob);
};

const runMediaSync = async (
  userId: string | number,
  threadId: string,
  messages: SupportLocalMessage[],
  hasMore: boolean,
): Promise<void> => {
  const keepIds = collectAttachmentIdsForCache(messages, hasMore);
  await pruneAttachmentMediaCache(userId, keepIds);

  const attachments = collectAttachmentsForCache(messages, hasMore);

  for (const attachment of attachments) {
    if (!shouldCacheAttachment(attachment)) continue;
    if (await hasCachedAttachment(userId, attachment.id)) continue;
    try {
      await prefetchAttachment(userId, threadId, attachment);
    } catch {
      // фоновая предзагрузка — ошибки не критичны
    }
  }
};

/**
 * Синхронизирует IndexedDB с последними 20 сообщениями:
 * удаляет лишнее и предзагружает недостающие вложения в фоне.
 */
export const scheduleSupportMediaCacheSync = (
  userId: string | number,
  threadId: string,
  messages: SupportLocalMessage[],
  hasMore: boolean,
): void => {
  pendingSync = { userId, threadId, messages, hasMore };

  if (syncInFlight) {
    syncQueued = true;
    return;
  }

  const execute = async () => {
    while (pendingSync) {
      const job = pendingSync;
      pendingSync = null;
      syncQueued = false;
      await runMediaSync(job.userId, job.threadId, job.messages, job.hasMore);
      if (!syncQueued) break;
    }
  };

  syncInFlight = execute().finally(() => {
    syncInFlight = null;
  });
};

/** Быстрый resolve blob: IndexedDB → сеть → сохранение в IndexedDB. */
export const resolveSupportAttachmentBlob = async (
  userId: string | number,
  threadId: string | null,
  attachment: SupportAttachmentDTO,
): Promise<Blob> => {
  const cached = await getCachedAttachmentBlob(userId, attachment.id);
  if (cached) return cached;

  const blob = await fetchSupportAttachmentBlob(attachment.id);

  if (threadId && shouldCacheAttachment(attachment, blob.size)) {
    void putCachedAttachmentBlob(userId, threadId, attachment, blob);
  }

  return blob;
};
